<template>
  <aside
    class="app_sidebar"
    :class="{ 'app_sidebar--collapsed': collapsed, 'app_sidebar--mobile': isMobile }"
  >
    <div
      class="app_sidebar__brand"
      :class="{ 'app_sidebar__brand--collapsed': collapsed }"
    >
      <button
        type="button"
        class="app_sidebar__logo-btn"
        :class="{ 'app_sidebar__chrome--hide': collapsed }"
        aria-label="Ir a página principal"
        :tabindex="collapsed ? -1 : 0"
        @click="$emit('go-home')"
      >
        <img
          v-if="companyLogoUrl"
          class="app_sidebar__logo-img"
          :src="companyLogoUrl"
          alt="Logo"
        >
        <img
          v-else
          class="app_sidebar__logo-img"
          src="../../assets/img/logo.png"
          alt="Logo"
        >
      </button>
      <button
        type="button"
        class="app_sidebar__expand-btn"
        :class="{ 'app_sidebar__chrome--hide': !collapsed }"
        aria-label="Expandir menú"
        title="Expandir menú"
        :tabindex="collapsed ? 0 : -1"
        @click="$emit('toggle-sidebar')"
      >
        <fuse-icon name="panel-left-open" :size="iconLg" />
      </button>
    </div>

    <nav class="app_sidebar__nav" aria-label="Menú principal">
      <template v-if="showMenu">
        <template v-for="(item, i) in items">
          <div
            v-if="item.section_title && !collapsed"
            :key="'section-' + item.section_title + '-' + i"
            class="app_sidebar__section-title"
          >
            {{ item.section_title }}
          </div>

          <div
            v-if="item.section_title && collapsed"
            :key="'divider-' + item.section_title + '-' + i"
            class="app_sidebar__section-divider"
            aria-hidden="true"
          />

          <div
            :key="'item-' + i"
            class="app_sidebar__block"
            :class="{ 'app_sidebar__block--inactive': !item.active }"
            @mouseenter="onItemEnter(item, i, $event)"
            @mouseleave="onItemLeave"
          >
            <button
              v-if="item.dropdown"
              type="button"
              class="app_sidebar__item"
              :class="{
                'app_sidebar__item--active-parent': item.selected,
                'app_sidebar__item--open': item.expanded && !collapsed,
                'app_sidebar__item--flyout-open': flyoutIndex === i
              }"
              :title="collapsed ? null : item.title"
              :disabled="!item.active"
              @click="onDropdownClick(item, i, $event)"
            >
              <span class="app_sidebar__item-main">
                <fuse-icon
                  class="app_sidebar__item-icon"
                  :name="iconName(item.component)"
                  :size="iconMd"
                />
                <span class="app_sidebar__item-label">{{ item.title }}</span>
              </span>
              <fuse-icon
                class="app_sidebar__chevron"
                :name="item.expanded ? 'chevron-up' : 'chevron-down'"
                :size="iconSm"
              />
            </button>

            <button
              v-else
              type="button"
              class="app_sidebar__item"
              :class="{ 'app_sidebar__item--active': item.selected }"
              :title="collapsed ? null : item.title"
              :disabled="!item.active"
              @click="onSimpleClick(item)"
            >
              <span class="app_sidebar__item-main">
                <fuse-icon
                  class="app_sidebar__item-icon"
                  :name="iconName(item.component)"
                  :size="iconMd"
                />
                <span class="app_sidebar__item-label">{{ item.title }}</span>
              </span>
            </button>

            <ul
              v-if="item.dropdown && item.expanded && !collapsed && item.sub_items && item.sub_items.length"
              class="app_sidebar__subnav"
            >
              <li
                v-for="(subItem, j) in item.sub_items"
                :key="'sub-' + i + '-' + j"
                class="app_sidebar__subitem"
                :class="{
                  'app_sidebar__subitem--active': subItem.selected,
                  'app_sidebar__subitem--inactive': item.active && !subItem.active
                }"
                @click="subItem.active && navigate(subItem, subItem.route)"
              >
                {{ subItem.title }}
              </li>
            </ul>
          </div>
        </template>
      </template>
    </nav>

    <p v-if="!collapsed" class="app_sidebar__version">v {{ appVersion }}</p>
  </aside>
</template>

<script>
import FuseIcon from './FuseIcon.vue'
import { resolveLucideName, ICON_SIZE } from './iconMap'

const FLYOUT_CLOSE_DELAY = 180

export default {
  name: 'AppSidebar',
  components: { FuseIcon },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    companyLogoUrl: {
      type: String,
      default: ''
    },
    appVersion: {
      type: String,
      default: 'dev'
    },
    showMenu: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    flyoutIndex: null,
    closeTimer: null,
    iconMd: ICON_SIZE.md,
    iconSm: ICON_SIZE.sm,
    iconLg: ICON_SIZE.lg
  }),
  watch: {
    collapsed(value) {
      if (!value) this.closeFlyout()
    }
  },
  beforeDestroy() {
    this.clearCloseTimer()
    this.unbindFlyoutListeners()
  },
  methods: {
    iconName(componentKey) {
      return resolveLucideName(componentKey)
    },
    onItemEnter(item, index, event) {
      if (!this.collapsed || !item.active) return
      this.clearCloseTimer()
      const anchor = event.currentTarget.querySelector('.app_sidebar__item') || event.currentTarget
      this.openFlyout(index, anchor)
    },
    onItemLeave() {
      if (!this.collapsed) return
      this.scheduleCloseFlyout()
    },
    onFlyoutEnter() {
      this.clearCloseTimer()
    },
    onFlyoutLeave() {
      this.scheduleCloseFlyout()
    },
    onDropdownClick(item, index, event) {
      if (!item.active) return
      if (this.collapsed) {
        this.clearCloseTimer()
        this.openFlyout(index, event.currentTarget, true)
        return
      }
      this.closeFlyout()
      this.$emit('toggle-item', item)
    },
    onSimpleClick(item) {
      if (!item.active) return
      this.closeFlyout()
      this.navigate(item, item.route)
    },
    navigate(item, route) {
      this.$emit('navigate', item, route)
    },
    openFlyout(index, anchorEl, fromClick) {
      if (!fromClick && this.flyoutIndex === index) return
      if (fromClick && this.flyoutIndex === index) {
        this.closeFlyout()
        return
      }
      this.flyoutIndex = index
      const item = this.items[index]
      if (!item || !anchorEl) return
      const rect = anchorEl.getBoundingClientRect()
      const gap = 10
      let top = rect.top
      const left = rect.right + gap
      const estimatedHeight = item.dropdown
        ? 56 + (item.sub_items || []).length * 42
        : 48
      const maxTop = window.innerHeight - estimatedHeight - 12
      if (top > maxTop) top = Math.max(12, maxTop)
      this.$emit('flyout', {
        item,
        style: { top: top + 'px', left: left + 'px' }
      })
      this.$nextTick(() => this.bindFlyoutListeners())
    },
    scheduleCloseFlyout() {
      this.clearCloseTimer()
      this.closeTimer = setTimeout(() => {
        this.closeFlyout()
      }, FLYOUT_CLOSE_DELAY)
    },
    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    closeFlyout() {
      this.clearCloseTimer()
      this.flyoutIndex = null
      this.$emit('flyout', null)
      this.unbindFlyoutListeners()
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.closeFlyout()
    },
    bindFlyoutListeners() {
      this.unbindFlyoutListeners()
      document.addEventListener('keydown', this.onDocumentKeydown, true)
    },
    unbindFlyoutListeners() {
      document.removeEventListener('keydown', this.onDocumentKeydown, true)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/layout/AppSidebar.vue